// SPDX-FileCopyrightText: Copyright 2024 Dash0 Inc.

"use client";

import { ComponentPropsWithoutRef, useContext } from "react";

import { HubResourceContext } from "@/hub/components/mdx/hub-resource-context";
import { cn, stylesMerge } from "@/lib/utils";

export function Img({ src, title, alt, className, style, ...rest }: ComponentPropsWithoutRef<"img">) {
	const context = useContext(HubResourceContext);

	if (!src || typeof src !== 'string') {
		return null;
	}

	let resolvedSrc = src;
	let width, height, aspectRatio: string | undefined;

	// Handle hub-specific image paths
	if (!src.startsWith("https://") && context?.resourceId && context?.resourceKind) {
		const indexEntry = context.index[context.resourceKind][context.resourceId];
		const path = indexEntry?.path;
		if (
				path &&
				// The images property was added at a later time, and we might still be running into a stale cache. Can be removed
				// mid 2025.
				context.index.images
		) {
			if (src.startsWith("/")) {
				src = src.slice(1);
			}
			const imageMetadata = context.index.images[`${path}/${src}`];
			if (imageMetadata) {
				// Divide by two to account for retina-ready images (that we require)
				width = `min(100%, ${imageMetadata.width / 2}px)`;
				height = `min(100%, ${imageMetadata.height / 2}px)`;
				aspectRatio = `${imageMetadata.width} / ${imageMetadata.height}`;
				resolvedSrc = `/hub/image?kind=${encodeURIComponent(context.resourceKind)}&id=${encodeURIComponent(context.resourceId)}&path=${encodeURIComponent(src)}`;
			}
		}
	}

	// We don't yet have appropriate width/height metadata about the images, and consequently cannot use next/image (yet).
	// There are ideas to extend the hub's index with image metadata support such that we can always use next/image,
	// but we haven't done that yet
	return (
			// eslint-disable-next-line @next/next/no-img-element
			<img
					src={resolvedSrc}
					alt={alt || ""}
					title={title}
					className={cn("block max-w-full", className)}
					style={stylesMerge(
							{
								width,
								height,
								aspectRatio,
							},
							style
					)}
					{...rest}
			/>
	);
}